import { ChangeDetectionStrategy, Component, effect, inject, input, OnDestroy, OnInit, signal } from '@angular/core';
import { collection, Firestore, onSnapshot, query, where } from '@angular/fire/firestore';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom, Subscription } from 'rxjs';
import { AuthService } from 'src/app/auth.service';
import { ConfirmMenuService } from 'src/app/shared/services/confirm-menu/confirm-menu.service';
import { TenantService } from 'src/app/shared/services/tenant/tenant.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { CallControlsComponent } from '../call-controls/call-controls.component';
import { CallInvitationComponent } from '../call-invitation/call-invitation.component';
import { CallStatusEvent, CallStatusService } from '../call-status.service';
import { ChatBoxComponent } from '../chat-box/chat-box.component';
import { ChimeCallService } from '../chime-call.service';
import { ChimeService } from '../chime.service';
import { JoinCallSettingsComponent } from '../join-call-settings/join-call-settings.component';
import { ParticipantListComponent } from '../participant-list/participant-list.component';
import { VideoCallStateService } from '../video-call-state.service';
import { VideoTileComponent } from '../video-tile/video-tile.component';

@Component({
  selector: 'app-chime-video-call-drawer',
  standalone: true,
  imports: [
    FormsModule,
    TranslateModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    JoinCallSettingsComponent,
    CallControlsComponent,
    CallInvitationComponent,
    ParticipantListComponent,
    ChatBoxComponent,
    VideoTileComponent,
  ],
  templateUrl: './chime-video-call-drawer.component.html',
  styleUrls: ['./chime-video-call-drawer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChimeVideoCallDrawerComponent implements OnInit, OnDestroy {
  chimeService = inject(ChimeService);
  chimeCallService = inject(ChimeCallService);
  authService = inject(AuthService);
  toast = inject(ToastService);
  translate = inject(TranslateService);
  callStatusService = inject(CallStatusService);
  videoCallStateService = inject(VideoCallStateService);
  tenantService = inject(TenantService);
  firestore = inject(Firestore);
  snackBar = inject(MatSnackBar);
  confirmMenuService = inject(ConfirmMenuService);

  constructor() {
    effect(() => {
      const isHost = this.isHost();
      const callId = this.callID();
      const tenantId = this.chimeService.tenantId() || this.tenantService.activeTenantId();
      if (isHost && callId && tenantId) {
        this.setupHostLobbyListener(callId, tenantId);
      } else {
        this.cleanupHostLobbyListener();
      }
    });
  }

  // --- Inputs ---
  isDrawer = input(true);
  callID = input.required<string>();

  // --- Local State ---
  loading = signal(false);
  error = signal<string | null>('');
  username = signal(this.authService.userInfo()?.fullName || 'Anonymous');
  chatOpen = this.chimeService.isChatVisible;
  callStatusMessage = signal<string | null>(null);
  pstnPhoneNumber = signal<string | null>(null);
  participantsOpen = signal(false);
  waitingParticipants = signal<any[]>([]);
  private callStatusSub: Subscription | null = null;
  private hostLobbySubscription: any = null;

  // --- Derived State from Service ---
  isMeetingActive = this.chimeService.isMeetingActive;
  participants = this.chimeService.participants;
  isHost = this.chimeService.isHost;
  localParticipant = this.chimeService.localParticipant;
  remoteParticipants = this.chimeService.remoteParticipants;
  localUserId = this.chimeService.localUserId;
  isScreenSharing = this.chimeService.isScreenSharing;
  screenShareTile = this.chimeService.screenShareTile;
  activeSpeakerId = this.chimeService.activeSpeakerId;
  isRecording = this.chimeService.isRecording;
  isVideoFxActive = this.chimeService.isVideoFxActive;

  ngOnInit(): void {
    const callID = this.callID();
    if (callID) {
      this.chimeService.fetchMeetingSettings(callID);
    }
  }

  async onJoinCall(): Promise<void> {
    if (this.loading() || !this.username().trim()) return;
    this.loading.set(true);
    this.error.set(null);
    try {
      await this.chimeService.joinMeeting(this.callID(), this.username(), true);
    } catch (err) {
      console.error(err);
      this.error.set('Failed to join the call. Please try again.');
    } finally {
      this.loading.set(false);
    }
  }

  onLeaveCall(): void {
    // The host ending the call should end it for everyone.
    this.chimeCallService.endCallAndMeeting(this.callID()).subscribe({
      next: () => {
        console.log('Call and meeting ended successfully.');
        this.chimeService.leaveMeeting(); // Perform local cleanup
        this.chatOpen.set(false);
      },
      error: (err) => {
        console.error('Failed to end call and meeting:', err);
        // Still perform local cleanup even if backend fails
        this.chimeService.leaveMeeting();
        this.chatOpen.set(false);
      },
    });
  }

  onToggleScreenShare(mode?: 'content-only' | 'with-sidebar' | 'full'): void {
    if (this.isScreenSharing()) {
      this.chimeService.stopScreenShare();
    } else {
      this.chimeService.startScreenShare(mode || 'content-only');
    }
  }

  onToggleRecording(): void {
    if (this.isRecording()) {
      this.chimeService.stopRecording(this.callID());
    } else {
      this.chimeService.startRecording(this.callID());
    }
  }

  onMuteParticipant(attendeeId: string): void {
    this.chimeService.muteParticipant(attendeeId);
  }

  onToggleVideoFx(): void {
    this.chimeService.toggleBackgroundBlur();
  }

  async onStartPstnCall(phoneNumber: string): Promise<void> {
    this.pstnPhoneNumber.set(phoneNumber);
    this.callStatusMessage.set(`${phoneNumber} ${this.translate.instant('VideoCall.Status.Dialing')}`);

    try {
      const transactionId = await this.chimeService.startPstnCall(phoneNumber, this.callID());

      if (transactionId) {
        const tenantId = this.chimeService.tenantId() || this.tenantService.activeTenantId() || 'unknown';
        this.callStatusService.connect(tenantId, this.callID(), transactionId);
        this.callStatusSub?.unsubscribe();
        this.callStatusSub = this.callStatusService.callStatus$.subscribe((event) => {
          if (event) {
            this.updateCallStatusMessage(event);
          } else {
            this.callStatusMessage.set(null);
          }
        });
      } else {
        throw new Error('Failed to get transactionId.');
      }
    } catch (error) {
      console.error('Failed to start PSTN call flow:', error);
      this.callStatusMessage.set('Call failed to start.');
      setTimeout(() => this.callStatusMessage.set(null), 5000);
    }
  }

  closeDrawer() {
    this.videoCallStateService.closeDrawer();
  }

  private updateCallStatusMessage(event: CallStatusEvent): void {
    const phone = event.ToPhoneNumber;
    const phonePrefix = phone ? `${phone} ` : '';
    switch (event.EventType) {
      case 'NEW_OUTBOUND_CALL':
      case 'RINGING':
        this.callStatusMessage.set(`${phonePrefix}${this.translate.instant('VideoCall.Status.Ringing')}`);
        this.chimeService.playRingingSound();
        break;
      case 'CALL_ANSWERED':
        this.callStatusMessage.set(`${phonePrefix}${this.translate.instant('VideoCall.Status.Answered')}`);
        this.chimeService.stopRingingSound();
        setTimeout(() => this.callStatusMessage.set(null), 1000);
        break;
      case 'HANGUP':
        this.callStatusMessage.set(`${phonePrefix}${this.translate.instant('VideoCall.Status.Ended')}`);
        this.chimeService.playHangupSound();
        this.chimeService.attendeesToRemove.update((list) => [...list, event.AttendeeId]);
        console.log('PSTN call ended.', event);
        setTimeout(() => this.callStatusMessage.set(null), 1000);
        break;
      case 'CALL_FAILED':
        this.chimeService.stopRingingSound();
        this.callStatusMessage.set(
          `${phonePrefix}${this.translate.instant('VideoCall.Status.Failed', { error: event.error || this.translate.instant('VideoCall.Status.UnknownReason') })}`
        );
        setTimeout(() => this.callStatusMessage.set(null), 5000);
        break;
      default:
        // Do nothing for other events for now
        break;
    }
  }

  onHangupPstnAttendee(event: MouseEvent, attendeeId: string): void {
    event.stopPropagation();
    const trigger = event.currentTarget as HTMLElement;
    this.confirmMenuService.confirm(trigger, 'Global.Confirm').subscribe(() => {
      this.chimeService.kickParticipant(attendeeId);
    });
  }

  private setupHostLobbyListener(callId: string, tenantId: string) {
    this.cleanupHostLobbyListener();
    let isInitialSnapshot = true;
    const lobbyCollRef = collection(this.firestore, 'workspace-events', tenantId, 'contents', callId, 'lobby');
    const q = query(lobbyCollRef, where('status', '==', 'waiting'));
    this.hostLobbySubscription = onSnapshot(q, (snapshot) => {
      const newWaiting = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as any);

      if (!isInitialSnapshot) {
        const hasNewWaiters = snapshot.docChanges().some(change => change.type === 'added');
        if (hasNewWaiters) {
          this.chimeService.playChimeSound();
        }
      }
      isInitialSnapshot = false;

      this.waitingParticipants.set(newWaiting);
    });
  }

  private cleanupHostLobbyListener() {
    if (this.hostLobbySubscription) {
      this.hostLobbySubscription();
      this.hostLobbySubscription = null;
    }
    this.waitingParticipants.set([]);
  }

  async onAdmitParticipant(guestUserId: string) {
    const callId = this.callID();
    if (!callId) return;
    const guest = this.waitingParticipants().find((w) => w.id === guestUserId);
    if (!guest) return;
    try {
      await firstValueFrom(this.chimeCallService.admitParticipant(callId, guestUserId, guest.name, guest.email));
    } catch (err) {
      console.error('Failed to admit participant:', err);
    }
  }

  async onRejectParticipant(guestUserId: string) {
    const callId = this.callID();
    if (!callId) return;
    try {
      await firstValueFrom(this.chimeCallService.rejectParticipant(callId, guestUserId));
    } catch (err) {
      console.error('Failed to reject participant:', err);
    }
  }

  ngOnDestroy(): void {
    this.callStatusService.disconnect();
    this.callStatusSub?.unsubscribe();
    this.cleanupHostLobbyListener();
    if (this.chimeService.videoPreviewIsActive()) {
      this.chimeService.stopVideoPreview();
    }
    this.chimeService.leaveMeeting();
  }
}
